<template>
    <div>
        <h6 class="mb-3">
            <MessageSquare :size="18" class="me-2" style="vertical-align: text-bottom;" />
            Messages
        </h6>

        <!-- New Message Form -->
        <div class="card mb-3">
            <div class="card-body p-2">
                <div class="row g-2">
                    <div class="col-md-6">
                        <tech-ac
                            v-model="newMessage.RecipientUserId"
                            placeholder="Recipient (optional)"
                            inputclass="form-control form-control-sm"
                        />
                    </div>
                    <div class="col-md-6">
                        <div class="form-check form-check-inline mt-2">
                            <input class="form-check-input" type="checkbox" id="isPrivate" v-model="newMessage.IsPrivate">
                            <label class="form-check-label" for="isPrivate">
                                <small>Private Message</small>
                            </label>
                        </div>
                    </div>
                </div>
                <div class="mt-2">
                    <textarea 
                        class="form-control form-control-sm" 
                        rows="2" 
                        v-model="newMessage.Body"
                        placeholder="Type a message..."
                        @keydown.ctrl.enter="sendMessage"
                    ></textarea>
                </div>
                <div class="d-flex justify-content-end mt-2">
                    <button 
                        class="btn btn-sm btn-primary" 
                        @click="sendMessage" 
                        :disabled="!newMessage.Body || sending"
                    >
                        <Send :size="14" class="me-1" />
                        {{ sending ? 'Sending...' : 'Send' }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Messages List -->
        <div v-if="loading" class="text-center py-3">
            <div class="spinner-border spinner-border-sm" role="status"></div>
            <small class="ms-2 text-muted">Loading messages...</small>
        </div>

        <div v-else-if="messages.length === 0" class="text-muted text-center py-3">
            <MessageSquare :size="24" class="mb-2 d-block mx-auto opacity-50" />
            <small>No messages yet.</small>
        </div>

        <div v-else class="messages-list">
            <div 
                v-for="msg in messages" 
                :key="msg.Id" 
                class="card mb-2"
                :class="{ 'border-warning': msg.IsPrivate }"
            >
                <div class="card-body p-2">
                    <div class="d-flex justify-content-between align-items-start">
                        <div>
                            <strong class="small">{{ msg.SenderName || 'System' }}</strong>
                            <span v-if="msg.RecipientName" class="text-muted small ms-1">
                                → {{ msg.RecipientName }}
                            </span>
                            <span v-if="msg.IsPrivate" class="badge bg-warning text-dark ms-1" style="font-size: 0.65rem;">
                                <Lock :size="10" class="me-1" />Private
                            </span>
                        </div>
                        <div class="d-flex align-items-center gap-2">
                            <small class="text-muted">{{ formatDate(msg.CreatedDate) }}</small>
                            <button
                                class="btn btn-outline-secondary btn-sm py-0 px-1"
                                style="font-size: 0.7rem;"
                                @click="startReply(msg)"
                                title="Reply"
                            >
                                <Reply :size="11" />
                            </button>
                        </div>
                    </div>
                    <div class="small mt-1" style="white-space: pre-wrap;">{{ msg.Body }}</div>
                </div>
            </div>
        </div>

        <!-- Inline reply box (appears when replying to a specific message) -->
        <div v-if="replyTarget" class="card mt-2 border-primary">
            <div class="card-body p-2">
                <div class="d-flex justify-content-between align-items-center mb-1">
                    <small class="text-muted">Replying to <strong>{{ replyTarget.SenderName || 'System' }}</strong></small>
                    <button class="btn-close" style="font-size: 0.6rem;" @click="replyTarget = null; replyBody = ''"></button>
                </div>
                <textarea
                    class="form-control form-control-sm mb-2"
                    rows="3"
                    v-model="replyBody"
                    placeholder="Type your reply..."
                    @keydown.ctrl.enter="sendInlineReply"
                ></textarea>
                <div class="d-flex justify-content-end gap-2">
                    <button class="btn btn-sm btn-outline-secondary" @click="replyTarget = null; replyBody = ''">Cancel</button>
                    <button class="btn btn-sm btn-primary" @click="sendInlineReply" :disabled="!replyBody || sendingReply">
                        <span v-if="sendingReply">Sending...</span>
                        <span v-else><Send :size="12" class="me-1" />Reply</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch, onUnmounted } from "vue";
import ApiService from "@/services/ApiService";
import Toast from "@/services/Toast";
import TechAc from "@/components/Techs/TechAc.vue";
import { MessageSquare, Send, Lock, Reply } from "lucide-vue-next";

const props = defineProps({
    entityId: {
        type: Number,
        default: 0
    },

    entityType: {
        type: String,
        default: "Request"
    },
});

const messages = ref<any[]>([]);
const loading = ref(false);
const sending = ref(false);
const replyTarget = ref<any>(null);
const replyBody = ref('');
const sendingReply = ref(false);
const newMessage = ref({
    Body: '',
    RecipientUserId: null as number | null,
    IsPrivate: false
});

const unwatch: Function[] = [];

const loadMessages = async () => {
    if (!props.entityId) return;
    loading.value = true;
    try {
        const response: any = await ApiService.getNotifications({
            RelatedEntityType: props.entityType || 'Request',
            RelatedEntityId: props.entityId,
            Mailbox: 'all',
            PageSize: 999999,
            SortDirection: 'asc'
        }, true);
        const result = response?.Data || [];
        messages.value = Array.isArray(result) ? result : [];
    } catch (error) {
        console.error('Error loading messages:', error);
        messages.value = [];
    } finally {
        loading.value = false;
    }
};

const getSubjectType = () => {
    switch (props.entityType) {
        case 'Request': return 'Job';
        case 'Ask': return 'Request';
        default: return props.entityType || 'Job';
    }
};

const sendMessage = async () => {
    if (!newMessage.value.Body || !props.entityId) return;
    sending.value = true;
    try {
        await ApiService.saveNotification({
            Subject: getSubjectType() + ' Message',
            Body: newMessage.value.Body,
            NotificationType: 'Message',
            RelatedEntityType: props.entityType || 'Request',
            RelatedEntityId: props.entityId,
            RecipientUserId: newMessage.value.RecipientUserId || null,
            IsPrivate: newMessage.value.IsPrivate
        });
        newMessage.value.Body = '';
        newMessage.value.RecipientUserId = null;
        newMessage.value.IsPrivate = false;
        await loadMessages();
    } catch (error) {
        console.error('Error sending message:', error);
    } finally {
        sending.value = false;
    }
};

const startReply = (msg: any) => {
    replyTarget.value = msg;
    replyBody.value = '';
};

const sendInlineReply = async () => {
    if (!replyBody.value || !replyTarget.value || !props.entityId) return;
    sendingReply.value = true;
    try {
        const payload: any = {
            Subject: replyTarget.value.Subject ? `Re: ${replyTarget.value.Subject}` : `Re: ${getSubjectType()} Message`,
            Body: replyBody.value,
            NotificationType: 'Message',
            RelatedEntityType: props.entityType || 'Request',
            RelatedEntityId: props.entityId,
            RecipientUserId: replyTarget.value.SenderUserId || null,
        };
        if (replyTarget.value.ConversationId) {
            payload.ConversationId = replyTarget.value.ConversationId;
        } else {
            payload.ParentNotificationId = replyTarget.value.Id;
        }
        await ApiService.saveNotification(payload);
        replyBody.value = '';
        replyTarget.value = null;
        await loadMessages();
    } catch (error) {
        console.error('Error sending reply:', error);
        Toast.error('Failed to send reply');
    } finally {
        sendingReply.value = false;
    }
};

const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleString();
};

unwatch.push(watch(() => props.entityId, (nv) => {
    if (nv) loadMessages();
}, { immediate: true }));

onUnmounted(() => {
    unwatch.forEach(fn => fn());
});
</script>
